import { Settings } from "lucide-react";
import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ModuleSettingsToggle } from "@/lib/module-registry";
import type { AppRoute } from "@/lib/routes";
import { cn } from "@/lib/utils";

/**
 * A module's own settings, behind one gear in the sidebar footer.
 *
 * **Why a gear rather than a nav section.** Settings used to render as a
 * labeled section with an expandable "Settings" group inside it — so reaching
 * Pricing meant scanning past a section header to a row that only expands,
 * then picking a child: three levels for occasional configuration, sitting at
 * the same visual weight as the daily work above it. Every module that grows
 * settings would repeat that cost, and the section's position drifted with
 * whatever else the module declared.
 *
 * The gear fixes the position instead of the label. It lives in the footer
 * next to `UserMenu` in every module, so "where are this module's settings"
 * has one answer that doesn't move — the pattern VS Code, Slack, and Linear
 * all use for workspace-level configuration.
 *
 * **Distinct from the topbar gear** (`AdminEntryButton`), which is
 * cross-tenant Admin: users, permission sets, feature flags, catalogs. This
 * one is scoped to the module you're in and only appears when that module
 * declares settings the caller may see. The tooltip and `aria-label` both
 * name the module to keep them apart
 * for anyone navigating by screen reader.
 *
 * ## The three settings scopes — where a new setting goes
 *
 * Settled by an audit of all ten `/settings/*` pages (2026-09-03). Scope a
 * setting by **what it edits**, never by which code reads it: consumer-
 * tracing makes almost everything look deal-scoped, because almost
 * everything eventually touches a deal.
 *
 * 1. **Platform / tenant config** → the Admin module (`AdminEntryButton`,
 *    topbar gear). Users, permission sets, feature flags, and the shared
 *    catalogs — including Product Catalog (it edits *products*) and
 *    Reference Data (it edits *benchmark datasets*).
 * 2. **Module config** → this gear. Settings that tune one module's own
 *    pages: Matrix Adder Defaults and Pass-through Rates for Matrix
 *    Pricing, Portal Links for supplier guest links, Scoring Model for the
 *    product-fit rubric.
 * 3. **Record config** (accounts / clients / deals) → **the inline grid
 *    controls**, not a gear. `ViewPicker` + `ViewActionsMenu` +
 *    `ColumnsPopover` in the grid's own header bar
 *    (`components/grid-views/`).
 *
 * Tier 3 deliberately has no gear. The only record-scoped configuration
 * that exists today is saved grid views, and those are **per-user and
 * ungated** — `grid_views.rs` enforces only `existing.user_id !=
 * session.user_id → NotFound` — whereas every page behind a gear is
 * `requiresSuperAdmin`. Putting per-user preferences behind a control that
 * elsewhere means admin-only config would break the meaning of the gear.
 * The inline controls also sit next to the grid they reconfigure, which is
 * where the user already is.
 *
 * So: a genuinely record-scoped, admin-gated setting has no home yet
 * (candidates would be admin-editable column/chip registries, or CRUD over
 * `ref.industries` / `ref.deal_types`, none of which exist). Design that
 * pattern against the real thing when it arrives rather than building an
 * empty third gear now.
 *
 * **A setting too small for a page is a toggle in the popover itself**
 * (`ModuleDefinition.settingsToggles`), beneath the page links — Reports'
 * "Enable Emails" switch. The toggle owns its data and saves on flip;
 * the popover only places it.
 *
 * Renders nothing when `items` and `toggles` are both empty, so a module
 * whose settings are all filtered out by auth shows no dead control.
 * `ModuleSidebar` checks the same condition to drop the bordered strip that
 * wraps this — the guard here is kept so the component is safe to mount
 * anywhere, not just behind that check.
 */
export function ModuleSettingsMenu({
  moduleLabel,
  items,
  toggles = [],
  compact,
  onClose,
}: {
  /** The owning module's label, for the tooltip and `aria-label`. */
  moduleLabel: string;
  /** Already auth-filtered settings routes. Rendered in the given order. */
  items: AppRoute[];
  /** Already auth-filtered toggles, rendered beneath the routes. */
  toggles?: ModuleSettingsToggle[];
  /** True in the sidebar's icons-only mode. */
  compact: boolean;
  /** Closes the mobile drawer — called after picking an item. */
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  if (items.length === 0 && toggles.length === 0) return null;

  // Lights the gear while any of its own items is the current route, so the
  // footer still shows where you are once the popover closes. `startsWith`
  // rather than `useMatch` per item: this is one control standing in for
  // several paths, and a settings page's own subpaths belong to it.
  // Cross-links are excluded: they point at a page another module owns, so
  // matching one would light this module's gear while the reader is somewhere
  // else entirely — and that module's own gear would light too.
  const isActive = items.some(
    (i) =>
      !i.crossLink && (pathname === i.path || pathname.startsWith(`${i.path}/`)),
  );

  const label = `${moduleLabel} settings`;

  const trigger = (
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className={cn(
          compact
            ? "flex h-10 w-10 items-center justify-center rounded-md"
            : "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm",
          "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "data-[state=open]:bg-row-hover",
          isActive
            ? "font-semibold text-primary"
            : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
        )}
      >
        <Settings size={compact ? 20 : 16} strokeWidth={1.75} />
        {!compact && <span>Settings</span>}
      </button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {compact ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right">{label}</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}

      {/* `side="top"` so the list opens up into the sidebar rather than off
          the bottom of the viewport — the footer is flush with it. Matches
          `UserMenu`, the other footer popover. */}
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[240px] p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-eyebrow text-text-dim">
          {label}
        </div>
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              // A cross-link leaves this module for a page another one owns,
              // so it opens beside the reader's place rather than replacing
              // it. `rel` is required with `_blank`: without it the opened
              // page gets a handle on this one through `window.opener`.
              target={item.crossLink ? "_blank" : undefined}
              rel={item.crossLink ? "noreferrer" : undefined}
              onClick={() => {
                setOpen(false);
                onClose();
              }}
              className={({ isActive: rowActive }) =>
                cn(
                  "flex items-center gap-2.5 rounded-md px-2 py-2 text-sm no-underline transition-colors",
                  rowActive
                    ? "bg-nav-active-bg font-semibold text-primary"
                    : "text-muted-foreground hover:bg-row-hover hover:text-foreground",
                )
              }
            >
              {Icon && <Icon size={16} strokeWidth={1.75} />}
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}
        {toggles.length > 0 && items.length > 0 && (
          <div role="separator" className="my-1 border-t border-border" />
        )}
        {toggles.map((t) => (
          <div key={t.key}>{t.element}</div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
